import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useClearRefinements, useHierarchicalMenu, useSearchBox } from 'react-instantsearch';
import { LanguageContext } from '../../../../context/LanguageContext';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import {
  mapHierarchicalValueToCategoryId,
  mapCategoryIdToHierarchicalValue,
} from '../../../../util/dateRangeHelpers';
import { getVisibleCategoryIds } from '../../../../util/storeCategoryVisibility';
import {
  sortHierarchicalMenuItemsByOrder,
  filterHierarchicalMenuByVisibility,
  composeCategoryList,
  decorateTreeWithLiveFacets,
  STORE_CATEGORY_HIERARCHICAL_ATTRIBUTES,
  HIERARCHICAL_SEPARATOR,
} from '../../Sidebar/CategoryFilter/categoryFilterPipeline';
import { useStoreCategoryFacetTree } from '../../Sidebar/CategoryFilter/useStoreCategoryFacetTree';
import css from './CategoryPicker.module.css';
import { IconCollection, NamedLink } from '../../../../components';

const SCROLL_STEP_PX = 200;

/** Flatten hierarchical menu item and descendants for dropdown rows */
const flattenMenuItemOptions = item => {
  const out = [];
  const walk = (node, depth) => {
    out.push({ node, depth });
    (node.data || []).forEach(child => walk(child, depth + 1));
  };
  walk(item, 0);
  return out;
};

/** Deepest label in tree matching full hierarchical value */
const findLabelForHierarchicalValue = (items, targetValue) => {
  if (!targetValue || !items?.length) return null;
  for (const item of items) {
    if (item.value === targetValue) return item.label;
    if (targetValue.startsWith(`${item.value}${HIERARCHICAL_SEPARATOR}`)) {
      const nested = findLabelForHierarchicalValue(item.data || [], targetValue);
      if (nested) return nested;
    }
  }
  return null;
};

/** Selection is under this root if refined path starts with root value */
const isSelectionUnderRoot = (rootItem, selectedHierarchicalValue) => {
  if (!selectedHierarchicalValue) return false;
  if (selectedHierarchicalValue === rootItem.value) return true;
  const prefix = `${rootItem.value}${HIERARCHICAL_SEPARATOR}`;
  return selectedHierarchicalValue.startsWith(prefix);
};

const CategoryPicker = ({ user, selectedCategoryFilter, onCategoryChange, scrollActiveCategory }) => {
  const intl = useIntl();
  const { locale } = React.useContext(LanguageContext);
  const publicData = user?.attributes?.profile?.publicData || {};
  const storeCategories = publicData.storeCategories || [];
  const storeCategoryGroups = publicData.storeCategoryGroups || {};
  const hasStoreCategories = storeCategories.length > 0;
  const hasStoreCategoryGroups = storeCategoryGroups?.__order?.length > 0;
  const isMarketplaceOnlyMode = !hasStoreCategories && !hasStoreCategoryGroups;
  const showAllCategories = publicData.showAllCategories !== false;
  const showAllCustomCategories = publicData.showAllCustomCategories !== false;
  const isShowAllMode = showAllCategories;

  const { visibleCategoryIds, visibleMarketplaceKeys } = useMemo(
    () => getVisibleCategoryIds(storeCategories, storeCategoryGroups),
    [storeCategories, storeCategoryGroups]
  );

  // Live, query-scoped menu — drives refine() and, once decorated in, the
  // per-pill counts and the "this category has results for your search" highlight.
  const { items, refine } = useHierarchicalMenu({
    attributes: STORE_CATEGORY_HIERARCHICAL_ATTRIBUTES,
    limit: 100,
    showMore: false,
    separator: HIERARCHICAL_SEPARATOR,
  });

  const { canRefine, refine: clearRefine } = useClearRefinements();

  // Full store catalogue, independent of the keyword — so the strip keeps showing
  // every category while the shopper types instead of collapsing to the one match.
  const { fullFacetTree } = useStoreCategoryFacetTree(user, locale);
  const { query } = useSearchBox();
  const keywordActive = !!(query && query.trim());

  // Only swap in the full catalogue while a keyword search is active — that's the
  // case where the live menu collapses (RAS-2591). With no keyword the live menu
  // already lists every category, so the default view is left exactly as-is.
  // Falls back to the live menu if the catalogue fetch is still pending or failed.
  const structureItems = useMemo(
    () =>
      keywordActive && fullFacetTree && fullFacetTree.length
        ? fullFacetTree
        : items ?? [],
    [keywordActive, fullFacetTree, items]
  );

  const sortedItems = useMemo(
    () => sortHierarchicalMenuItemsByOrder(structureItems, user, locale),
    [structureItems, user, locale]
  );

  const filteredItems = useMemo(
    () =>
      filterHierarchicalMenuByVisibility(
        sortedItems,
        user,
        locale,
        visibleCategoryIds,
        visibleMarketplaceKeys
      ),
    [sortedItems, user, locale, visibleCategoryIds, visibleMarketplaceKeys]
  );

  const renderedItems = useMemo(() => {
    const composed = isMarketplaceOnlyMode
      ? structureItems
      : isShowAllMode
        ? composeCategoryList(
            filteredItems,
            sortedItems,
            user,
            locale,
            storeCategoryGroups,
            showAllCategories,
            showAllCustomCategories
          )
        : showAllCustomCategories
          ? filteredItems
          : [];
    return decorateTreeWithLiveFacets(composed, items, keywordActive);
  }, [
    isMarketplaceOnlyMode,
    isShowAllMode,
    structureItems,
    items,
    keywordActive,
    filteredItems,
    sortedItems,
    user,
    locale,
    storeCategoryGroups,
    showAllCategories,
    showAllCustomCategories,
  ]);

  const lastRefinedValueRef = useRef(null);
  const [openRootValue, setOpenRootValue] = useState(null);
  const [pendingOpenRootValue, setPendingOpenRootValue] = useState(null);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const rootButtonRefs = useRef(new Map());
  const openDelayTimerRef = useRef(null);

  const selectedHierarchicalValue = useMemo(() => {
    if (!selectedCategoryFilter?.categoryId || !user) return null;
    return mapCategoryIdToHierarchicalValue(
      selectedCategoryFilter.categoryId,
      selectedCategoryFilter.depth,
      user,
      locale
    );
  }, [selectedCategoryFilter?.categoryId, selectedCategoryFilter?.depth, user, locale]);

  const appliedPath = useMemo(() => {
    if (!selectedHierarchicalValue) return [];
    const labels = selectedHierarchicalValue
      .split(HIERARCHICAL_SEPARATOR)
      .map(v => v.trim())
      .filter(Boolean);
    if (labels.length === 0) {
      const fallbackLabel =
        findLabelForHierarchicalValue(renderedItems, selectedHierarchicalValue) ||
        selectedCategoryFilter?.categoryId;
      return fallbackLabel ? [{ label: fallbackLabel, value: selectedHierarchicalValue }] : [];
    }
    return labels.map((label, idx) => ({
      label,
      value: labels.slice(0, idx + 1).join(HIERARCHICAL_SEPARATOR),
    }));
  }, [selectedHierarchicalValue, renderedItems, selectedCategoryFilter?.categoryId]);

  // Root pill that owns the current explicit selection, used to scroll it into the
  // leftmost position (RAS-2548) — distinct from scrollActiveCategory, which only
  // tracks scroll-spy highlighting when no explicit filter is applied.
  const selectedRootValue = useMemo(() => {
    if (!selectedHierarchicalValue || !renderedItems?.length) return null;
    const match = renderedItems.find(item => isSelectionUnderRoot(item, selectedHierarchicalValue));
    return match ? match.value : null;
  }, [selectedHierarchicalValue, renderedItems]);

  const updateScrollAffordance = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    updateScrollAffordance();
    el.addEventListener('scroll', updateScrollAffordance, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => requestAnimationFrame(updateScrollAffordance)) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollAffordance);
      ro?.disconnect();
    };
  }, [updateScrollAffordance, renderedItems]);

  useEffect(() => {
    const onDoc = e => {
      if (!rootRef.current?.contains(e.target)) {
        if (openDelayTimerRef.current) {
          clearTimeout(openDelayTimerRef.current);
          openDelayTimerRef.current = null;
        }
        setOpenRootValue(null);
        setPendingOpenRootValue(null);
        setMenuStyle(null);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const updateMenuPositionForRoot = useCallback(rootValue => {
    const el = rootButtonRefs.current.get(rootValue);
    if (!el) return;
    const rect = el.getBoundingClientRect();

    // position:fixed is normally viewport-relative, but any ancestor with a CSS
    // transform / will-change:transform / filter becomes the containing block instead.
    // Walk up the DOM to find that ancestor and subtract its offset so the dropdown
    // lands directly below the button on both desktop (no transform) and mobile
    // (MobileResultsFiltersBar has transform:translate3d(0,0,0) for GPU compositing).
    let offsetTop = 0;
    let offsetLeft = 0;
    let node = el.parentElement;
    while (node && node !== document.documentElement) {
      const style = window.getComputedStyle(node);
      const hasTransform = style.transform && style.transform !== 'none';
      const hasWillChange = style.willChange && style.willChange.includes('transform');
      const hasFilter = style.filter && style.filter !== 'none';
      if (hasTransform || hasWillChange || hasFilter) {
        const ancestorRect = node.getBoundingClientRect();
        offsetTop = ancestorRect.top;
        offsetLeft = ancestorRect.left;
        break;
      }
      node = node.parentElement;
    }

    setMenuStyle({
      top: `${Math.round(rect.bottom + 6 - offsetTop)}px`,
      left: `${Math.round(rect.left - offsetLeft)}px`,
      width: `${Math.round(rect.width)}px`,
    });
  }, []);

  useEffect(() => {
    if (!openRootValue) return undefined;
    updateMenuPositionForRoot(openRootValue);
    const onWin = () => updateMenuPositionForRoot(openRootValue);
    window.addEventListener('scroll', onWin, true);
    window.addEventListener('resize', onWin);
    return () => {
      window.removeEventListener('scroll', onWin, true);
      window.removeEventListener('resize', onWin);
    };
  }, [openRootValue, updateMenuPositionForRoot]);

  useEffect(() => {
    return () => {
      if (openDelayTimerRef.current) {
        clearTimeout(openDelayTimerRef.current);
      }
    };
  }, []);

  // Auto-scroll the pill strip to keep the scroll-active category visible.
  // Only fires when no explicit Algolia filter is applied.
  useEffect(() => {
    if (!scrollActiveCategory || selectedCategoryFilter?.categoryId) return;
    const btn = rootButtonRefs.current.get(scrollActiveCategory);
    if (btn) btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [scrollActiveCategory, selectedCategoryFilter?.categoryId]);

  // Explicit category selection: scroll it to the leftmost visible position (RAS-2548),
  // not just "into view" — makes the active category the primary visible item.
  useEffect(() => {
    if (!selectedRootValue) return;
    const btn = rootButtonRefs.current.get(selectedRootValue);
    if (btn) btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }, [selectedRootValue]);

  // Sync app state (URL-backed) -> Algolia refinement (same rules as CategoryFilter)
  useEffect(() => {
    let isMounted = true;

    if (!selectedCategoryFilter?.categoryId) {
      if (lastRefinedValueRef.current !== null) {
        clearRefine();
        lastRefinedValueRef.current = null;
      }
      return () => {
        isMounted = false;
      };
    }

    if (!user) {
      return () => {
        isMounted = false;
      };
    }

    if (selectedCategoryFilter.depth > 0 && !hasStoreCategories) {
      return () => {
        isMounted = false;
      };
    }

    if (hasStoreCategories) {
      const isMarketplaceRoot =
        selectedCategoryFilter.depth === 0 && selectedCategoryFilter.categoryId?.startsWith('mp:');
      const isVisible =
        isMarketplaceRoot && isShowAllMode
          ? true
          : selectedCategoryFilter.depth === 0
            ? visibleMarketplaceKeys.has(selectedCategoryFilter.categoryId)
            : visibleCategoryIds.has(selectedCategoryFilter.categoryId);
      if (!isVisible) {
        if (isMounted) {
          clearRefine();
          lastRefinedValueRef.current = null;
          onCategoryChange(null, null);
        }
        return () => {
          isMounted = false;
        };
      }
    }

    const hierarchicalValue = mapCategoryIdToHierarchicalValue(
      selectedCategoryFilter.categoryId,
      selectedCategoryFilter.depth,
      user,
      locale
    );

    if (!hierarchicalValue) {
      if (isMounted) {
        clearRefine();
        lastRefinedValueRef.current = null;
        onCategoryChange(null, null);
      }
      return () => {
        isMounted = false;
      };
    }

    if (lastRefinedValueRef.current === hierarchicalValue) {
      return () => {
        isMounted = false;
      };
    }

    lastRefinedValueRef.current = hierarchicalValue;

    try {
      refine(hierarchicalValue);
    } catch (error) {
      console.error('CategoryPicker: Failed to refine Algolia search', error);
      if (isMounted) {
        lastRefinedValueRef.current = null;
      }
      return () => {
        isMounted = false;
      };
    }

    return () => {
      isMounted = false;
    };
  }, [
    selectedCategoryFilter?.categoryId,
    selectedCategoryFilter?.depth,
    user,
    hasStoreCategories,
    storeCategories,
    visibleCategoryIds,
    visibleMarketplaceKeys,
    isShowAllMode,
    locale,
    refine,
    clearRefine,
    onCategoryChange,
  ]);

  const isOptionChecked = useCallback(
    value => {
      if (!selectedHierarchicalValue) return false;
      return (
        selectedHierarchicalValue === value ||
        selectedHierarchicalValue.startsWith(`${value}${HIERARCHICAL_SEPARATOR}`)
      );
    },
    [selectedHierarchicalValue]
  );

  const handleMenuItemClick = item => {
    const isExactSelected = selectedHierarchicalValue === item.value;
    if (isExactSelected) {
      clearRefine();
      onCategoryChange(null, null);
      setOpenRootValue(null);
      setPendingOpenRootValue(null);
      setMenuStyle(null);
      return;
    }
    const categoryIdDepth = mapHierarchicalValueToCategoryId(item.value, user, locale);
    if (categoryIdDepth) {
      onCategoryChange(categoryIdDepth.categoryId, categoryIdDepth.depth);
      const hasChildren = Array.isArray(item.data) && item.data.length > 0;
      if (!hasChildren) {
        setOpenRootValue(null);
        setPendingOpenRootValue(null);
        setMenuStyle(null);
      }
    }
  };

  const handleAppliedChipClear = useCallback(
    clearIdx => {
      if (clearIdx <= 0) {
        clearRefine();
        onCategoryChange(null, null);
        return;
      }
      const retained = appliedPath[clearIdx - 1];
      if (!retained?.value) {
        clearRefine();
        onCategoryChange(null, null);
        return;
      }
      const categoryIdDepth = mapHierarchicalValueToCategoryId(retained.value, user, locale);
      if (categoryIdDepth) {
        clearRefine();
        onCategoryChange(categoryIdDepth.categoryId, categoryIdDepth.depth);
      }
    },
    [appliedPath, clearRefine, locale, onCategoryChange, user]
  );

  const handleLeafRootClick = rootItem => {
    const isExactSelected = selectedHierarchicalValue === rootItem.value;
    if (isExactSelected) {
      clearRefine();
      onCategoryChange(null, null);
      return;
    }
    const categoryIdDepth = mapHierarchicalValueToCategoryId(rootItem.value, user, locale);
    if (categoryIdDepth) {
      onCategoryChange(categoryIdDepth.categoryId, categoryIdDepth.depth);
    }
  };

  const handleRootClick = (rootItem, isOpen, underThisRoot) => {
    const isPendingOpen = pendingOpenRootValue === rootItem.value;
    if (!underThisRoot) {
      const categoryIdDepth = mapHierarchicalValueToCategoryId(rootItem.value, user, locale);
      if (categoryIdDepth) {
        onCategoryChange(categoryIdDepth.categoryId, categoryIdDepth.depth);
      }
    }

    // Toggle closed when root already open/refined or waiting to open.
    if ((isOpen && underThisRoot) || isPendingOpen) {
      if (openDelayTimerRef.current) {
        clearTimeout(openDelayTimerRef.current);
        openDelayTimerRef.current = null;
      }
      setOpenRootValue(null);
      setPendingOpenRootValue(null);
      setMenuStyle(null);
    } else {
      // Delay opening slightly so Algolia can return child options first.
      setPendingOpenRootValue(rootItem.value);
      if (openDelayTimerRef.current) {
        clearTimeout(openDelayTimerRef.current);
      }
      openDelayTimerRef.current = setTimeout(() => {
        setOpenRootValue(rootItem.value);
        setPendingOpenRootValue(null);
        updateMenuPositionForRoot(rootItem.value);
        openDelayTimerRef.current = null;
      }, 250);
    }
  };

  // Promote pending open root after Algolia updates with children.
  useEffect(() => {
    if (!pendingOpenRootValue) return;
    const pendingRoot = renderedItems.find(it => it.value === pendingOpenRootValue);
    if (!pendingRoot) return;
    const hasChildren = Array.isArray(pendingRoot.data) && pendingRoot.data.length > 0;
    const refinedUnderRoot = isSelectionUnderRoot(pendingRoot, selectedHierarchicalValue);
    if (refinedUnderRoot && hasChildren) {
      if (openDelayTimerRef.current) {
        clearTimeout(openDelayTimerRef.current);
        openDelayTimerRef.current = null;
      }
      setOpenRootValue(pendingOpenRootValue);
      setPendingOpenRootValue(null);
      updateMenuPositionForRoot(pendingOpenRootValue);
    }
  }, [pendingOpenRootValue, renderedItems, selectedHierarchicalValue, updateMenuPositionForRoot]);

  const scrollByDirection = dir => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * SCROLL_STEP_PX, behavior: 'smooth' });
  };

  if (!renderedItems || renderedItems.length === 0) {
    if (selectedCategoryFilter?.categoryId) {
      // clear url category filter option
      return <div className={css.clearCategoryFilter}>
        <span className={css.clearCategoryFilterLabel}>
          Opps! wrong category selected.
        </span>
        <NamedLink className={css.clearCategoryFilterLink} name="ProfilePage" params={{ id: user.id.uuid }}>
          Clear
        </NamedLink>
      </div>
    }
    return null;
  }


  return (
    <div className={css.root} ref={rootRef}>
      <div className={css.rowOuter}>
        {canScrollLeft && <button
          type="button"
          className={css.arrowButton}
          aria-label={intl.formatMessage({ id: 'CategoryPicker.scrollCategoriesLeft' })}
          disabled={!canScrollLeft}
          onClick={() => scrollByDirection(-1)}
        >
          <IconCollection name="arrow_left_cirlcle_line" />
        </button>}
        <div className={css.scrollTrack} ref={scrollRef}>
          <div className={css.scrollInner}>
            {renderedItems.map(rootItem => {
              const isOpen = openRootValue === rootItem.value;
              const underThisRoot =
                selectedHierarchicalValue && isSelectionUnderRoot(rootItem, selectedHierarchicalValue);
              const options = flattenMenuItemOptions(rootItem);
              const isLeaf = options.length <= 1;
              return (
                <div key={rootItem.value} className={css.dropdownWrap}>
                  <button
                    ref={el => {
                      if (el) rootButtonRefs.current.set(rootItem.value, el);
                      else rootButtonRefs.current.delete(rootItem.value);
                    }}
                    type="button"
                    className={classNames(
                      css.categoryDropdownButton,
                      isLeaf && css.categoryLeafButton,
                      (underThisRoot ||
                        rootItem.isRefined ||
                        (keywordActive && (rootItem.matched || rootItem.matchedDescendant))) &&
                        css.activeCategory,
                      !selectedCategoryFilter?.categoryId && scrollActiveCategory === rootItem.value && css.scrollActiveCategory
                    )}
                    onClick={() =>
                      isLeaf
                        ? handleLeafRootClick(rootItem)
                        : handleRootClick(rootItem, isOpen, underThisRoot)
                    }
                  >
                    <span
                      className={classNames(css.rootButtonMain, isLeaf && css.rootButtonMainLeaf)}
                    >
                      <span className={css.rootButtonLabel}>{rootItem.label}</span>
                      {rootItem.count != null ? (
                        <span className={css.optionMeta}>({rootItem.count})</span>
                      ) : null}
                    </span>
                    {!isLeaf ? <IconCollection name="arrow_down" /> : null}
                  </button>
                  {isOpen && !isLeaf ? (
                    <div className={css.dropdownMenu} style={menuStyle || undefined}>
                      {pendingOpenRootValue === rootItem.value ? (
                        <div className={css.dropdownEmpty}>Loading options...</div>
                      ) : options.length > 1 ? (
                        options.map(({ node, depth }) => {
                          const isChecked = isOptionChecked(node.value);
                          const isExactSelected =
                            selectedHierarchicalValue &&
                            node.value === selectedHierarchicalValue;
                          return (
                            <button
                              key={node.value}
                              type="button"
                              className={classNames(
                                css.dropdownItem,
                                css.subDropdownItem,
                                isExactSelected && css.dropdownItemActive,
                                isChecked && css.dropdownItemChecked
                              )}
                              style={{ paddingLeft: `${12 + depth * 14}px` }}
                              onClick={() => handleMenuItemClick(node)}
                            >
                              <span className={css.optionCheckbox} aria-hidden="true">
                                {/* Keep a checkbox in DOM for semantics, but render custom SVG UI */}
                                <input
                                  type="checkbox"
                                  readOnly
                                  checked={!!isChecked}
                                  tabIndex={-1}
                                  aria-hidden="true"
                                />

                                {isChecked ? (
                                  <IconCollection name="checkbox_checked_purple" />

                                ) : (
                                  <IconCollection name="checkbox_unchecked_purple" />

                                )}
                              </span>
                              <span className={css.optionLabel}>{node.label}</span>
                              {node.count != null ? (
                                <span className={css.optionMeta}>({node.count})</span>
                              ) : null}
                            </button>
                          );
                        })
                      ) : (
                        <div className={css.dropdownEmpty}>No child options available</div>
                      )}
                      <button
                        type="button"
                        className={css.dropdownClear}
                        onClick={() => {
                          clearRefine();
                          onCategoryChange(null, null);
                          setOpenRootValue(null);
                          setPendingOpenRootValue(null);
                          setMenuStyle(null);
                        }}
                      >
                        <FormattedMessage id="StoreCategoryFilter.clear" defaultMessage="Clear" />
                        &nbsp;<i className='ri-refresh-line' />
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
        {canScrollRight ? (
          <button
            type="button"
            className={css.arrowButton}
            aria-label={intl.formatMessage({ id: 'CategoryPicker.scrollCategoriesRight' })}
            onClick={() => scrollByDirection(1)}
          >
            <IconCollection name="arrow_right_circle_line" />
          </button>
        ) : null}
      </div>

      {selectedCategoryFilter?.categoryId && appliedPath.length > 0 ? (
        <div className={css.appliedRow}>
          {/* <span className={css.appliedLabel}>
            <FormattedMessage id="StoreCategoryFilter.selected" defaultMessage="Applied" />
          </span> */}
          {appliedPath.map(({ label, value }, idx) => (
            <button
              key={`${value}-${idx}`}
              type="button"
              className={css.appliedChip}
              disabled={!canRefine}
              onClick={() => handleAppliedChipClear(idx)}
            >
              <span>{label}</span>
              <i className={classNames('ri-close-line', css.appliedClearIcon)} />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

CategoryPicker.propTypes = {
  user: PropTypes.object.isRequired,
  selectedCategoryFilter: PropTypes.shape({
    categoryId: PropTypes.string,
    depth: PropTypes.number,
  }),
  onCategoryChange: PropTypes.func.isRequired,
  scrollActiveCategory: PropTypes.string,
};

export default CategoryPicker;
